import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControlLabel,
  Checkbox,
  Paper
} from '@material-ui/core';
import { CloudUpload, GetApp, CheckCircle, Error, Warning } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { green, red } from '@material-ui/core/colors';
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  uploadBox: {
    border: '2px dashed #ccc',
    borderRadius: '4px',
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.3s ease',
    '&:hover': {
      borderColor: '#009E60',
    },
    '&.dragOver': {
      borderColor: '#009E60',
      backgroundColor: '#f5f5f5',
    },
    '&.disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
      backgroundColor: '#f5f5f5',
      '&:hover': {
        borderColor: '#ccc',
      }
    }
  },
  fileInput: {
    display: 'none',
  },
  uploadIcon: {
    fontSize: '3rem',
    color: '#009E60',
    marginBottom: theme.spacing(1),
  },
  resultSection: {
    marginTop: theme.spacing(2),
  },
  successChip: {
    backgroundColor: green[500],
    color: 'white',
  },
  errorChip: {
    backgroundColor: red[500],
    color: 'white',
  },
  downloadTemplate: {
    marginBottom: theme.spacing(2),
  },
  successAlert: {
    backgroundColor: green[50],
    border: `1px solid ${green[200]}`,
    padding: theme.spacing(2),
    borderRadius: '4px',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  errorAlert: {
    backgroundColor: red[50],
    border: `1px solid ${red[200]}`,
    padding: theme.spacing(2),
    borderRadius: '4px',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  alertIcon: {
    marginRight: theme.spacing(1),
  },
  warningAlert: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    padding: theme.spacing(2),
    borderRadius: '4px',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  warningInvalidUrls: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    padding: theme.spacing(2),
    borderRadius: '4px',
    marginBottom: theme.spacing(2),
  },
}));

const BulkUploadDialog = ({ open, onClose, onUploadSuccess, axiosConfig }) => {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [captureScreenshots, setCaptureScreenshots] = useState(true);

  const handleFileSelect = (file) => {
    if (uploading) return;
    
    const allowedTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (allowedTypes.includes(fileExt)) {
      setSelectedFile(file);
      setUploadResult(null);
    } else {
      alert('Please select a CSV or XLSX file');
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    
    if (uploading) return;
    
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!uploading) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleUploadBoxClick = () => {
    if (uploading) return;
    document.getElementById('bulk-file-input').click();
  };

  const bulkUploadFile = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('captureScreenshots', captureScreenshots);
      
      const response = await axios.post(
        '/resource/admin/bulk-upload-file', 
        formData, 
        axiosConfig
      );

      // response.data is already the parsed JSON, no need for await
      const result = response.data;

      console.log('File upload successful:', result);
      setUploadResult(result);
      if (onUploadSuccess) {
        onUploadSuccess(result);
      }
    } catch (error) {
      console.error('File upload error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || 
                           error.response.data?.error || 
                           `Server error: ${error.response.status}`;
        setUploadResult({ error: errorMessage });
      } else if (error.request) {
        // Request was made but no response received
        setUploadResult({ error: 'No response from server. Please check your connection.' });
      } else {
        // Something else happened
        setUploadResult({ error: error.message || 'An unexpected error occurred' });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setUploading(false);
    onClose();
  };

  const downloadTemplate = () => {
    // Create a sample CSV template with the new format
    const csvContent = `"Business Name","Business URL","AI Generated Summary","Category","Sub-Category","Suggestions","Predecessors","Companion Resources","Why They Pair Well","Resource Type","Code","Status","Owner Email","Owner Approval","Image URL"
"GitHub","https://github.com","Code repository and collaboration platform for software development","development","Version Control","Use for source code management, issue tracking, and team collaboration","SVN, Mercurial, CVS","GitLab, Bitbucket, Azure DevOps","All provide Git-based version control with similar workflows","platform","github","approved","admin@example.com","true","https://github.com/favicon.ico"
"Figma","https://figma.com","Collaborative design tool for UI/UX design and prototyping","design","Design Tools","Use for interface design, prototyping, and design system management","Sketch, Adobe XD, InVision","Miro, Notion, Slack","Streamline design-to-development workflow and team communication","software-as-a-service","figma","approved","admin@example.com","true",""
"Notion","https://notion.so","All-in-one workspace for notes, docs, and project management","productivity","Documentation","Use for project management, knowledge base, and team documentation","OneNote, Confluence, Trello","Figma, Slack, GitHub","Integrate design, development, and project management workflows","software-as-a-service","notion","approved","","",""
"Slack","https://slack.com","Team communication and collaboration platform","productivity","Communication","Use for team messaging, file sharing, and integrations","Microsoft Teams, Discord","Notion, GitHub, Figma","Enable seamless communication across all work tools","software-as-a-service","slack","approved","","",""`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resource_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <CloudUpload style={{ marginRight: '8px' }} />
          Bulk Upload Resources
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Download Template Section */}
        <Box className={classes.downloadTemplate}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Download a template file to see the required format:
          </Typography>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={downloadTemplate}
            size="small"
          >
            Download CSV Template
          </Button>
        </Box>

        <Divider style={{ margin: '16px 0' }} />

        {/* Time Warning */}
        <div className={classes.warningAlert}>
          <Typography variant="body2" style={{ color: '#856404' }}>
            <strong>Note:</strong> Bulk upload may take several minutes to complete, especially for large files. 
            Please be patient and do not close this dialog during the upload process.
          </Typography>
        </div>

        {/* File Upload Section */}
        <Paper
          className={`${classes.uploadBox} ${dragOver && !uploading ? 'dragOver' : ''} ${uploading ? 'disabled' : ''}`}
          elevation={0}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleUploadBoxClick}
        >
          <CloudUpload className={classes.uploadIcon} style={{ opacity: uploading ? 0.5 : 1 }} />
          <Typography variant="h6" gutterBottom style={{ opacity: uploading ? 0.5 : 1 }}>
            {uploading 
              ? 'Upload in progress...' 
              : selectedFile 
              ? selectedFile.name 
              : 'Drop your file here or click to browse'
            }
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ opacity: uploading ? 0.5 : 1 }}>
            {uploading ? 'Please wait while your file is being processed' : 'Supported formats: CSV, XLSX, XLS'}
          </Typography>
          {!uploading && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
              Maximum file size: 10MB
            </Typography>
          )}
        </Paper>

        <input
          id="bulk-file-input"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileInputChange}
          className={classes.fileInput}
          disabled={uploading}
        />

        {/* Screenshot Capture Option */}
        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={captureScreenshots}
                onChange={(e) => setCaptureScreenshots(e.target.checked)}
                color="primary"
                disabled={uploading}
              />
            }
            label="Capture screenshots for uploaded resources"
          />
        </Box>

        {/* Upload Progress */}
        {uploading && (
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              <strong>Processing your file...</strong> This may take several minutes depending on file size and number of resources.
            </Typography>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
              Please do not close this dialog or navigate away from the page.
            </Typography>
          </Box>
        )}

        {/* Upload Results */}
        {uploadResult && (
          <Box className={classes.resultSection}>
            {uploadResult.error ? (
              <div className={classes.errorAlert}>
                <Error className={classes.alertIcon} style={{ color: red[500] }} />
                <Box>
                  <Typography variant="subtitle2">Upload Failed</Typography>
                  <Typography variant="body2">{uploadResult.error}</Typography>
                </Box>
              </div>
            ) : (
              <>
                <div className={classes.successAlert}>
                  <CheckCircle className={classes.alertIcon} style={{ color: green[500] }} />
                  <Typography variant="subtitle2">
                    {(uploadResult.data && uploadResult.data.invalidUrls && uploadResult.data.invalidUrls.length > 0) || 
                     (uploadResult.data && uploadResult.data.summary?.validationErrors > 0)
                      ? 'Upload Completed - Review Required!' 
                      : 'Upload Successful!'}
                  </Typography>
                </div>
                
                {uploadResult.data && uploadResult.data.summary && (
                  <Box display="flex" flexWrap="wrap" mb={2} style={{ gap: '8px' }}>
                    <Chip 
                      label={`Total: ${uploadResult.data.summary.total || 0}`} 
                      size="small" 
                    />
                    <Chip 
                      label={`Processed: ${uploadResult.data.summary.processed || 0}`} 
                      size="small"
                      className={classes.successChip}
                    />
                    {uploadResult.data.summary.successful > 0 && (
                      <Chip 
                        label={`Created: ${uploadResult.data.summary.successful || 0}`} 
                        size="small"
                        className={classes.successChip}
                      />
                    )}
                    {uploadResult.data.summary.updated > 0 && (
                      <Chip 
                        label={`Updated: ${uploadResult.data.summary.updated || 0}`} 
                        size="small"
                        className={classes.successChip}
                      />
                    )}
                    {uploadResult.data.summary.failed > 0 && (
                      <Chip 
                        label={`Failed: ${uploadResult.data.summary.failed || 0}`} 
                        size="small"
                        className={classes.errorChip}
                      />
                    )}
                    {(uploadResult.data.summary.validationErrors > 0 || (uploadResult.data.invalidUrls && uploadResult.data.invalidUrls.length > 0)) && (
                      <Chip 
                        label={`Issues: ${uploadResult.data.summary.validationErrors || 0}`} 
                        size="small"
                        style={{ backgroundColor: '#f8f9fa', color: '#6c757d', border: '1px solid #dee2e6' }}
                      />
                    )}
                  </Box>
                )}

                {/* Issues Warning - Combined Invalid URLs and Validation Errors */}
                {uploadResult.data && ((uploadResult.data.invalidUrls && uploadResult.data.invalidUrls.length > 0) || 
                 (uploadResult.data.validationErrors && uploadResult.data.validationErrors.length > 0)) && (
                  <div className={classes.warningInvalidUrls}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Warning className={classes.alertIcon} style={{ color: '#6c757d' }} />
                      <Typography variant="subtitle2" style={{ color: '#495057' }}>
                        ⚠️ Action Required: Issues Found ({uploadResult.data.summary?.validationErrors || uploadResult.data.invalidUrls?.length || 0} resources)
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ color: '#6c757d', marginBottom: '12px', fontWeight: 500 }}>
                      The following resources need attention. Please review and update them:
                    </Typography>
                    <List dense style={{ backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '4px', maxHeight: '200px', overflow: 'auto' }}>
                      {/* Show Invalid URLs */}
                      {uploadResult.data.invalidUrls?.map((invalidUrl, index) => (
                        <ListItem key={`invalid-${index}`} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                Row {invalidUrl.row}: {invalidUrl.resourceName}
                              </Typography>
                            }
                            secondary={
                              <>
                                <Typography variant="caption" color="error" display="block">
                                  Invalid URL: {invalidUrl.invalidUrl}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Reason: {invalidUrl.reason}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                      {/* Show Validation Errors that are NOT already shown in Invalid URLs */}
                      {uploadResult.data.validationErrors?.filter(error => 
                        !uploadResult.data.invalidUrls?.some(invalidUrl => invalidUrl.row === error.row)
                      ).map((error, index) => (
                        <ListItem key={`validation-${index}`} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                Row {error.row}: {error.name}
                              </Typography>
                            }
                            secondary={
                              error.errors && error.errors.map((err, errIndex) => (
                                <Typography key={errIndex} variant="caption" color="error" display="block">
                                  {err}
                                </Typography>
                              ))
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="caption" style={{ color: '#6c757d', marginTop: '8px', display: 'block', fontStyle: 'italic' }}>
                      💡 Tip: You can edit these resources from the main Resources table after closing this dialog.
                    </Typography>
                  </div>
                )}

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Errors:
                    </Typography>
                    <List dense>
                      {uploadResult.errors.slice(0, 5).map((error, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`Row ${error.row || index + 1}: ${error.message || error}`}
                            secondary={error.data ? JSON.stringify(error.data) : ''}
                          />
                        </ListItem>
                      ))}
                      {uploadResult.errors.length > 5 && (
                        <ListItem>
                          <Typography variant="body2" color="textSecondary">
                            ... and {uploadResult.errors.length - 5} more errors
                          </Typography>
                        </ListItem>
                      )}
                    </List>
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={uploading}>
          {uploadResult ? 
            ((uploadResult.data && uploadResult.data.invalidUrls && uploadResult.data.invalidUrls.length > 0) || 
             (uploadResult.data && uploadResult.data.summary?.validationErrors > 0)
              ? 'Close (Review Issues Above)' 
              : 'Close')
            : 'Cancel'}
        </Button>
        {selectedFile && !uploading && !uploadResult && (
          <Button 
            onClick={bulkUploadFile} 
            color="primary" 
            variant="contained"
            style={{ backgroundColor: '#009E60' }}
          >
            Upload File
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadDialog; 

